<script setup lang="ts">
import { computed, ref, watch } from 'vue';

export interface DeleteWithReasonModalProps {
  confirmText?: string;
  description?: string;
  email?: string;
  loading?: boolean;
  modelValue: boolean;
  name: string;
  title?: string;
}

const props = withDefaults(defineProps<DeleteWithReasonModalProps>(), {
  confirmText: 'Confirmar',
  description: 'Você está prestes a inativar o usuário abaixo. Esta ação requer um motivo.',
  email: '',
  loading: false,
  title: 'Inativar registro',
});

const emit = defineEmits<{
  cancel: [];
  confirm: [reason: string];
  'update:modelValue': [value: boolean];
}>();

const reason = ref('');

const isVisible = computed({
  get: () => props.modelValue,
  set: (value: boolean) => emit('update:modelValue', value),
});

const canConfirm = computed(() => reason.value.trim().length > 0);

watch(() => props.modelValue, (open) => {
  if (!open) {
    reason.value = '';
  }
});

function handleClose() {
  if (props.loading) return;
  isVisible.value = false;
  emit('cancel');
}

function handleConfirm() {
  if (!canConfirm.value || props.loading) return;
  emit('confirm', reason.value.trim());
}
</script>

<template>
  <div
    v-if="isVisible"
    class="modal modal-blur fade show"
    style="display: block;"
    tabindex="-1"
    role="dialog"
    aria-modal="true"
    @keydown.escape="handleClose"
  >
    <button
      type="button"
      class="modal-backdrop fade show"
      aria-label="Fechar"
      :disabled="loading"
      @click="handleClose"
    />

    <div class="modal-dialog modal-dialog-centered" role="document">
      <div class="modal-content" @click.stop>
        <div class="modal-header">
          <h5 class="modal-title">{{ title }}</h5>
          <button
            type="button"
            class="btn-close"
            aria-label="Fechar"
            :disabled="loading"
            @click="handleClose"
          />
        </div>

        <div class="modal-body">
          <p class="text-secondary mb-3">
            {{ description }}
          </p>

          <div class="mb-3">
            <div class="datagrid">
              <div class="datagrid-item">
                <div class="datagrid-title">Nome</div>
                <div class="datagrid-content">{{ name }}</div>
              </div>
            </div>
          </div>

          <div v-if="email" class="mb-3">
            <div class="datagrid">
              <div class="datagrid-item">
                <div class="datagrid-title">E-mail</div>
                <div class="datagrid-content">{{ email }}</div>
              </div>
            </div>
          </div>

          <div class="mb-0">
            <label class="form-label required" for="deleteReason">Motivo</label>
            <textarea
              id="deleteReason"
              v-model="reason"
              class="form-control"
              rows="4"
              placeholder="Descreva o motivo da inativação"
              :disabled="loading"
            />
          </div>
        </div>

        <div class="modal-footer">
          <button
            type="button"
            class="btn btn-secondary"
            :disabled="loading"
            @click="handleClose"
          >
            Cancelar
          </button>
          <button
            type="button"
            class="btn btn-danger"
            :disabled="!canConfirm || loading"
            @click="handleConfirm"
          >
            <span
              v-if="loading"
              class="spinner-border spinner-border-sm me-2"
              role="status"
              aria-hidden="true"
            />
            {{ confirmText }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.modal {
  z-index: 1055;

  &.show {
    display: block !important;
  }

  .modal-dialog {
    position: relative;
    z-index: 1060;
  }
}

.modal-backdrop {
  z-index: 1050;
}
</style>
